import React, { useEffect, useState } from 'react';
import Modal from '../../../components/shared/Modal/Modal';
import LoadingSpinner from '../../../components/shared/LoadingSpinner/LoadingSpinner';
import { getBookingHistory } from '../../../services/bookings';
import type { BookingHistoryEntry } from '../../../types/api';

interface Props {
  /** Booking to show history for; the modal is open whenever this is set. */
  bookingId: number | null;
  onClose: () => void;
}

const ACTION_LABEL: Record<string, string> = {
  created: 'Created',
  edited: 'Edited',
  paid: 'Marked paid',
  transferred: 'Transferred',
};

/**
 * Modal showing a booking's merged change-log timeline: created, edits
 * (field old→new), paid, and transfers — newest first, each with who + when.
 */
const BookingHistoryModal: React.FC<Props> = ({ bookingId, onClose }) => {
  const [entries, setEntries] = useState<BookingHistoryEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (bookingId == null) {
      setEntries(null);
      setFailed(false);
      return;
    }
    let active = true;
    setEntries(null);
    setFailed(false);
    getBookingHistory(bookingId)
      .then((data) => { if (active) setEntries(data); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [bookingId]);

  return (
    <Modal isOpen={bookingId != null} onClose={onClose} title="Change history">
      {failed ? (
        <p className="text-sm text-red-600">Couldn't load history.</p>
      ) : entries === null ? (
        <div className="flex justify-center py-6"><LoadingSpinner /></div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-slate-500">No changes recorded yet.</p>
      ) : (
        <ol className="space-y-4">
          {entries.map((e, i) => (
            <li key={i} className="border-l-2 border-brand-200 pl-3">
              <div className="text-sm font-semibold text-slate-800">
                {ACTION_LABEL[e.action] ?? e.action} · {e.actor_name}
              </div>
              <div className="text-xs text-slate-500">
                {new Date(e.when).toLocaleString([], {
                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                })}
              </div>
              {Object.keys(e.changes).length > 0 ? (
                <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                  {Object.entries(e.changes).map(([field, c]) => (
                    <li key={field}>
                      <span className="font-medium">{field}</span>: {c.old ?? '—'} → {c.new ?? '—'}
                    </li>
                  ))}
                </ul>
              ) : e.summary ? (
                <div className="mt-1 text-xs text-slate-600">{e.summary}</div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
};

export default BookingHistoryModal;
